import { ChevronLeft } from 'lucide-react';

interface PrepHeaderProps {
  seasonName: string;
  totalCount: number;
  completedCount: number;
  zoneLabel?: string;
  weaponLabel: string;
  onBack: () => void;
}

const TICK_BAR_MAX = 60;

export function PrepHeader({
  seasonName,
  totalCount,
  completedCount,
  zoneLabel,
  weaponLabel,
  onBack,
}: PrepHeaderProps) {
  const useTicks = totalCount > 0 && totalCount <= TICK_BAR_MAX;
  const progressFraction = totalCount > 0 ? completedCount / totalCount : 0;

  const metaRight = zoneLabel ? `${zoneLabel} · ${weaponLabel}` : weaponLabel;

  return (
    <div
      className="sticky top-0 z-10 border-b-[0.5px] border-[var(--color-border)]/60"
      style={{ backgroundColor: 'rgba(28,26,23,0.92)', backdropFilter: 'blur(10px)' }}
      data-testid="prep-header"
    >
      {/* Row 1: back chevron + season title */}
      <div className="flex items-center gap-2 px-4 pt-4 pb-1">
        <button
          type="button"
          onClick={onBack}
          aria-label="Go back"
          data-testid="prep-header-back"
          className="flex-shrink-0 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
        >
          <ChevronLeft size={18} aria-hidden="true" />
        </button>
        <h1
          className="flex-1 min-w-0 font-display text-[20px] text-[var(--color-text-display)] truncate"
          style={{ letterSpacing: '-0.2px' }}
          data-testid="prep-header-title"
        >
          {seasonName}
        </h1>
      </div>

      {/* Row 2: fraction left + zone/weapon right */}
      <div className="flex items-baseline justify-between px-4 pb-2">
        <div className="flex items-baseline gap-1">
          <span
            className="font-display text-[var(--color-text-display)]"
            style={{ fontSize: '28px', fontVariantNumeric: 'tabular-nums' }}
            data-testid="prep-header-completed-count"
          >
            {completedCount}
          </span>
          <span className="text-[13px] text-[var(--color-text-muted)]">
            / {totalCount}
          </span>
          <span className="field-label ml-1">tasks</span>
        </div>
        <span className="field-label" data-testid="prep-header-meta-right">
          {metaRight}
        </span>
      </div>

      {/* Row 3: tick bar */}
      {totalCount > 0 && (
        <div className="px-4 pb-3" data-testid="prep-header-tick-bar">
          {useTicks ? (
            <div className="flex" style={{ gap: '1.5px' }}>
              {Array.from({ length: totalCount }, (_, i) => {
                const isFilled = i < completedCount;
                return (
                  <div
                    key={i}
                    data-tick-index={i}
                    data-tick-state={isFilled ? 'filled' : 'empty'}
                    style={{
                      flex: 1,
                      height: '2px',
                      backgroundColor: isFilled
                        ? 'var(--color-accent)'
                        : 'var(--color-border)',
                      opacity: isFilled ? 1 : 0.7,
                    }}
                  />
                );
              })}
            </div>
          ) : (
            <div
              className="relative overflow-hidden"
              style={{ height: '2px', backgroundColor: 'var(--color-border)', opacity: 0.7 }}
              data-testid="prep-header-tick-bar-single"
            >
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: `${progressFraction * 100}%`,
                  backgroundColor: 'var(--color-accent)',
                  opacity: 1,
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
